// src/utils/monitorMode.js
//
// Monitor-mode wording: the edge agent alerts and logs; it does not block or isolate.
// UI text may say "blocked" / "isolated" only when the event's real action says so.
//
// The event's real action is the matched rule's action, which the agent stamps on every
// policy event as `details.rule_action` (the description's "Action: alert." comes from it).
// The /topology/events API also exposes it as `action`, and derives `enforcement_event`
// from it (backend topology_router._is_enforcement).
//
// `action_on_violation` is NOT the action taken: it is the policy's declared intent, and
// policies named "Block …" carry action_on_violation=Block while their rule action is alert.

// Rule actions that, if ever stamped on an event, mean the agent acted on traffic.
export const ENFORCING_ACTIONS = ['block', 'isolate', 'quarantine', 'drop', 'enforce'];

// Rule actions that isolate a host (stronger than block).
const ISOLATING_ACTIONS = ['isolate', 'quarantine'];

const norm = (v) => String(v ?? '').trim().toLowerCase();

export const isEnforcingAction = (action) => ENFORCING_ACTIONS.includes(norm(action));

// The action the agent took for this event: topology `action`, else `details.rule_action`.
export const eventAction = (ev) => norm(ev?.action ?? ev?.details?.rule_action);

export const isEnforcedEvent = (ev) =>
  ev?.enforcement_event === true || isEnforcingAction(eventAction(ev));

export const isIsolatingEvent = (ev) => ISOLATING_ACTIONS.includes(eventAction(ev));

// Words in a policy's name/description that claim the policy acts on traffic.
const ENFORCEMENT_CLAIM =
  /\b(block|blocks|blocking|blocked|prevent|prevents|prevention|enforce|enforces|enforced|enforcement|isolate|isolates|isolation|quarantine|restrict|restricts|restricted)\b/i;

// Protocol nouns that contain a claim word but claim nothing. A maintained list:
// extend it when a policy text uses a protocol term that matches ENFORCEMENT_CLAIM.
const NON_CLAIM_PHRASES = [/control relay output block/gi];

const DECLARED_ENFORCING = ['block', 'isolate', 'quarantine'];

const textClaims = (text) => {
  let t = String(text || '');
  NON_CLAIM_PHRASES.forEach((re) => { t = t.replace(re, ''); });
  return ENFORCEMENT_CLAIM.test(t);
};

// Does the policy say it blocks/prevents/enforces (by name, description or declared action)?
export const policyClaimsEnforcement = ({ name, description, declaredAction } = {}) =>
  textClaims(name) || textClaims(description) || DECLARED_ENFORCING.includes(norm(declaredAction));

// Show "Enforcement: off — monitor mode" when the policy claims enforcement but none of its
// rule actions enforce. ruleActions: an assigned policy's rules[].action, or the fired
// events' rule_action values.
export const showEnforcementOff = ({ name, description, declaredAction, ruleActions = [] } = {}) =>
  policyClaimsEnforcement({ name, description, declaredAction }) &&
  !ruleActions.some(isEnforcingAction);

// Convenience adapters.
export const policyRecordEnforcementOff = (policy) =>
  showEnforcementOff({
    name: policy?.policy_name,
    description: policy?.description,
    declaredAction: policy?.action_on_violation,
    ruleActions: (policy?.rules || []).map((r) => r?.action),
  });

export const eventEnforcementOff = (ev) =>
  showEnforcementOff({
    name: ev?.policy_name ?? ev?.details?.policy_name,
    declaredAction: ev?.details?.action_on_violation,
    ruleActions: [eventAction(ev)],
  });

export const ENFORCEMENT_OFF_LABEL = 'Enforcement: off — monitor mode';

// Prepended to LLM prompts that summarize events, so generated text does not claim blocking.
export const MONITOR_MODE_PROMPT =
  'The edge agent runs in monitor mode: it alerts and logs, and an event\'s "action" field is what it actually did. ' +
  'A policy name that says Block is the policy\'s intent, not an action taken. Never say traffic was blocked or a host ' +
  'was isolated unless an event\'s action is block or isolate; you may recommend actions for the operator to take.\n';
